import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Accordion } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './styles.scss';
import { noop } from '../../common/propTypes';
import LoadingComponent from '../../common/LoadingComponent';
import {
    getPlacesRequested,
    setActivePlaceId,
    resetPlaceDetail,
} from './redux/actions';
import {
    getPlaces,
    getPlacesLoading,
    getHasMorePlaces,
    getActivePlaceId,
    getSearchedOnce,
} from './redux/selectors';
import { getUser } from '../auth/selectors';
import PlaceCard from './PlaceCard';
import LocationIcon from '../../icons/landlocationWhite.svg';

function PlaceList({
    places,
    onPlaceClick,
    activePlaceId,
    loading,
    hasMorePlaces,
    fetchPlaces,
    user,
    selectable,
    selectedUsers,
    onSelectUser,
    selectAll,
    tabId,
    resetPlaceDetail,
    searchedOnce,
}) {
    const [activePlaceIndex, setActivePlaceIndex] = useState(0);
    let filteredPlaces = places;
    if (selectable) {
        filteredPlaces = places.filter((p) => !p.isAssigned);
    } else if (tabId == 'assigned') {
        filteredPlaces = places.filter((p) => p.isAssigned);
    } else if (tabId == 'unassigned') {
        filteredPlaces = places.filter((p) => !p.isAssigned);
    }

    useEffect(() => {
        resetPlaceDetail();
    }, []);
    useEffect(() => {
        // eslint-disable-next-line react/prop-types
        places && places.length ? onPlaceClick(activePlaceId) : null;
    }, [places]);

    useEffect(() => {
        setActivePlaceIndex(activePlaceId);
    }, [activePlaceId]);

    const observer = useRef();
    const lastPlaceRef = useCallback(
        (node) => {
            if (loading) return; // if the current state of segments is loading we don't need to execute the callback
            if (observer.current) observer.current.disconnect(); // remove the old observer
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMorePlaces) {
                    fetchPlaces(); // if the last node is visible, callback is invoked to fetch more segments
                }
            });
            if (node) observer.current.observe(node); // last node / segment is used as the new observer
        },
        [loading, hasMorePlaces],
    );

    return (
        <>
            <div className="col-12 h-100">
                {filteredPlaces && filteredPlaces.length ? (
                    <Accordion activeKey={activePlaceIndex}>
                        {filteredPlaces.map(
                            (
                                {
                                    id,
                                    updatedBy,
                                    mapContent,
                                    researchCard,
                                    title,
                                    userId,
                                    status,
                                    address,
                                    researchProjectName,
                                    researchProjectId,
                                    createdAt,
                                    isAssigned,
                                    name,
                                    location,
                                    locationUrl,
                                    locationId,
                                },
                                index,
                            ) => {
                                return (
                                    <div
                                        ref={
                                            filteredPlaces.length === index + 1
                                                ? lastPlaceRef
                                                : null
                                        }
                                        key={`place-last-item-${id}`}
                                    >
                                        <PlaceCard
                                            status={status}
                                            address={address}
                                            researchProjectName={
                                                researchProjectName
                                            }
                                            researchProjectId={
                                                researchProjectId
                                            }
                                            createdAt={createdAt}
                                            isAssigned={isAssigned}
                                            id={id}
                                            updatedBy={updatedBy}
                                            title={title}
                                            mapContent={mapContent}
                                            researchCard={researchCard}
                                            onPlaceClick={(id) => {
                                                id == activePlaceIndex
                                                    ? onPlaceClick(-1)
                                                    : onPlaceClick(id);
                                            }}
                                            activePlaceId={activePlaceIndex}
                                            userId={userId}
                                            loggedInUser={user}
                                            selectable={selectable}
                                            selectedUsers={selectedUsers}
                                            onSelectUser={onSelectUser}
                                            selectAll={selectAll}
                                            name={name}
                                            location={location}
                                            locationUrl={locationUrl}
                                            locationId={locationId}
                                        />
                                    </div>
                                );
                            },
                        )}
                        <LoadingComponent isLoading={loading} />
                    </Accordion>
                ) : loading ? (
                    <LoadingComponent isLoading={loading} />
                ) : searchedOnce ? (
                    <div className="empty-card">
                        <div className="location-logo">
                            <img src={LocationIcon} />
                        </div>
                        <h4 className="text-center">No Record found</h4>
                    </div>
                ) : null}
            </div>
        </>
    );
}

PlaceList.propTypes = {
    places: PropTypes.array,
    fetchPlaces: PropTypes.func,
    onPlaceClick: PropTypes.func,
    activePlaceId: PropTypes.number,
    loading: PropTypes.bool,
    hasMorePlaces: PropTypes.bool,
    resetPlacesData: PropTypes.func,
    user: PropTypes.object,
    selectable: PropTypes.bool,
    onSelectUser: PropTypes.func,
    selectedUsers: PropTypes.object,
    tabId: PropTypes.string,
    selectAll: PropTypes.bool,
    resetPlaceDetail: PropTypes.func,
    searchedOnce: PropTypes.bool,
};

PlaceList.defaultProps = {
    places: [],
    fetchPlaces: noop,
    onPlaceClick: noop,
    activePlaceId: PropTypes.string,
    resetPlacesData: noop,
    selectable: false,
    selectedUsers: {},
    onSelectUser: noop,
    tabId: 'all',
    selectAll: false,
    searchedOnce: false,
};

const mapStateToProps = (state) => {
    return {
        places: getPlaces(state),
        loading: getPlacesLoading(state),
        hasMorePlaces: getHasMorePlaces(state),
        activePlaceId: getActivePlaceId(state),
        user: getUser(state),
        searchedOnce: getSearchedOnce(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchPlaces: () => dispatch(getPlacesRequested(true)),
        onPlaceClick: (id) => dispatch(setActivePlaceId(id)),
        resetPlaceDetail: () => dispatch(resetPlaceDetail()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PlaceList);
